// components/admin/useManualAttendance.js
//
// Shared state for the manual "Mark attendance" lists (AdminScreen's Events
// tab and EventDetailsScreen). Marking never removes a person from the list:
// a marked row flips to "Marked" and tapping it again undoes the mark. Both
// directions are optimistic and revert on a server error.
//
// mark/unmark resolve to `{ error }` (error is null on success) so the caller
// decides how to alert; `onCountsChanged` fires after every successful
// mark/unmark so the screen can refresh its event counts in the background.
import { useCallback, useEffect, useRef, useState } from "react";
import { AttendanceService } from "../../services/attendanceService";

const toSet = (set, id, on) => {
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
};

export function useManualAttendance({ eventId, adminId, onCountsChanged }) {
  const [markedIds, setMarkedIds] = useState(() => new Set());
  const [markingIds, setMarkingIds] = useState(() => new Set());
  const inFlight = useRef(new Set());
  // Session token, bumped on every reset: an event id alone can come back
  // (close the list, reopen the same event), so a late reply from the old
  // session must not touch the new one.
  const session = useRef(0);
  const countsRef = useRef(onCountsChanged);
  countsRef.current = onCountsChanged;

  // Per-event session: a different (or re-opened) event starts with a clean
  // slate, and late results from an earlier session are ignored.
  useEffect(() => {
    session.current += 1;
    inFlight.current = new Set();
    setMarkedIds(new Set());
    setMarkingIds(new Set());
  }, [eventId]);

  const run = useCallback(
    async (userId, nextMarked, call) => {
      if (!eventId || inFlight.current.has(userId)) return { error: null };
      const forSession = session.current;
      inFlight.current.add(userId);
      setMarkingIds((prev) => toSet(prev, userId, true));
      setMarkedIds((prev) => toSet(prev, userId, nextMarked));
      let error = null;
      try {
        ({ error } = await call());
      } catch (e) {
        error = e || new Error("Request failed");
      }
      if (session.current !== forSession) return { error: null };
      inFlight.current.delete(userId);
      setMarkingIds((prev) => toSet(prev, userId, false));
      if (error) setMarkedIds((prev) => toSet(prev, userId, !nextMarked));
      else countsRef.current?.();
      return { error };
    },
    [eventId]
  );

  const mark = useCallback(
    (userId) =>
      run(userId, true, async () => {
        const res = await AttendanceService.manuallyMarkAttendance(eventId, userId, adminId);
        // Already checked in (QR, or a mark from another device): that IS marked.
        return res.error?.code === "functions/already-exists" ? { error: null } : res;
      }),
    [run, eventId, adminId]
  );
  const unmark = useCallback(
    (userId) =>
      run(userId, false, () => AttendanceService.unmarkAttendance(eventId, userId)),
    [run, eventId]
  );

  return { markedIds, markingIds, mark, unmark };
}
